import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
  useMemo
} from 'react'
import { useAuth as useOIDCAuth } from 'react-oidc-context'
import mixpanel from 'mixpanel-browser'
import { SigninRedirectArgs, SignoutRedirectArgs } from 'oidc-client-ts'
import history from '../../lib/history'
import { UPDATE_LAST_LOGIN } from '../../graphql/users'
import { getStudentClient } from '../../lib/clients'
import { createUsers, getSelf, mapOIDCToAuth0User } from '../../lib/authenticationUtils'
import { AdminUserPermission } from '../../generated/graphql'
import { Auth0User, Roles } from '../../types'
import { getEnvironment } from '../../lib/environment'

export interface AuthContextState {
  user: any // Simplified user type, can be extended later
  isAuthenticated: boolean
  isInitializing: boolean
  signIn: (args?: SigninRedirectArgs | undefined) => void
  signOut: (args?: SignoutRedirectArgs | undefined) => void
  getTokenSilently: () => Promise<string | undefined>
}

export const AuthContext = createContext<AuthContextState | null>(null)

export const OIDCProviderWithSession = ({ children }: { children: ReactNode }) => {
  const {
    isAuthenticated,
    isLoading,
    user: oidcUser,
    signinRedirect,
    signoutRedirect
  } = useOIDCAuth()

  const [isInitializing, setIsInitializing] = useState(true)
  const [isLoadingUser, setIsLoadingUser] = useState(false)
  const [user, setUser] = useState<Auth0User | undefined>(undefined)
  const [hasFetchedClient, setHasFetchedClient] = useState(false)
  const [originalExpiresIn, setOriginalExpiresIn] = useState<number | null>(null)

  useEffect(() => {
    if (oidcUser && oidcUser.expires_in) {
      setOriginalExpiresIn(oidcUser.expires_in)
    }
  }, [oidcUser])

  // Provide token silently using signinSilent for the token retrieval
  const getTokenSilently = useCallback(async () => {
    const minExpiresIn = 60 // 1 minute threshold
    // Check if the token is valid and about to expire
    if (oidcUser && oidcUser.access_token) {
      const referenceExpiresIn = originalExpiresIn
      if (referenceExpiresIn && referenceExpiresIn < minExpiresIn) {
        // Remove user data locally to clear session and tokens
        signoutRedirect({
          extraQueryParams: {
            id_token_hint: oidcUser.id_token || ''
          }
        })
        return undefined // Return undefined to indicate no valid token
      }
      return oidcUser.access_token
    }
    return undefined
  }, [oidcUser, signoutRedirect, originalExpiresIn])

  const handleUserSession = useCallback(
    async (userProfile: any) => {
      let appUserId: number | undefined
      let { firstName } = userProfile
      let { lastName } = userProfile
      let permissions: Array<AdminUserPermission> | undefined

      try {
        setIsLoadingUser(true) // set as isLoadingUser until parses the user object
        const token = await getTokenSilently() // Retrieve the token
        if (token) {
          const client = getStudentClient(() => Promise.resolve(token))

          // First, try to create the user
          let createdUser: any = await createUsers(client, userProfile)

          if (!createdUser) {
            // If createUsers returns null, try to get the user with getSelf
            createdUser = await getSelf(client)
          }

          if (createdUser) {
            appUserId = createdUser?.appUserId || createdUser?.id
            permissions =
              createdUser?.effectivePermissions ||
              (createdUser?.permissions as Array<AdminUserPermission>)
            firstName = createdUser?.firstName || firstName
            lastName = createdUser?.lastName || lastName

            // Only apply adminUser or studentUser logic after getSelf
            if (createdUser?.studentUser) {
              firstName =
                createdUser?.studentUser?.displayName ||
                createdUser?.studentUser?.firstName ||
                firstName
              lastName = createdUser?.studentUser?.lastName || lastName
            } else if (createdUser?.adminUser) {
              firstName = createdUser?.adminUser?.firstName || firstName
              lastName = createdUser?.adminUser?.lastName || lastName
            }
          }

          if (createdUser) {
            await client.mutate({
              mutation: UPDATE_LAST_LOGIN
            })

            const roles = [Roles.admin]
            if (
              mixpanel &&
              roles.some((r) =>
                userProfile['https://api.student.civitaslearning.com/roles']?.includes(r)
              )
            ) {
              mixpanel.track('User Logged In', {
                institution: getEnvironment(),
                appUserId
              })
            }
          }
          setHasFetchedClient(true)
          setFormattedUser({ ...userProfile, appUserId, permissions, firstName, lastName })
          setIsLoadingUser(false)
        }
      } catch (error: any) {
        let errorType = 'generic'
        if (error.message === 'ACCOUNT_DISABLED' || error.message === 'ACCOUNT_NOT_FOUND') {
          errorType = 'account'
        }
        if (error.message === 'STUDENT_LOGIN_DISABLED') {
          errorType = 'student-login-disabled'
        }
        const emailQueryStringPart = userProfile.email
          ? `&email=${encodeURIComponent(userProfile.email)}`
          : ''
        errorType = encodeURIComponent(errorType)

        setIsLoadingUser(false)
        signoutRedirect({
          post_logout_redirect_uri: `${window.location.origin}?errorType=${errorType}${emailQueryStringPart}`,
          extraQueryParams: {
            id_token_hint: oidcUser?.id_token || ''
          }
        })
      }
    },
    [getTokenSilently, signoutRedirect, oidcUser]
  )

  const setFormattedUser = (userProfile: any) => {
    setUser({
      ...userProfile,
      roles: userProfile['https://api.student.civitaslearning.com/roles']
    })
  }

  useEffect(() => {
    setIsInitializing(isLoading || isLoadingUser)
  }, [isLoading, isLoadingUser])

  useEffect(() => {
    if (isAuthenticated) {
      // Elimina `code` y `state` de la URL
      const url = new URL(window.location.href)
      history.replace(url.pathname)
    }
  }, [isAuthenticated])

  // Fetch the token and log the client only when authenticated and the client hasn't been fetched yet
  useEffect(() => {
    const fetchAndLogClient = async () => {
      if (oidcUser) {
        const auth0User = mapOIDCToAuth0User(oidcUser)
        await handleUserSession(auth0User)
      }
    }

    if (isAuthenticated && !hasFetchedClient) {
      fetchAndLogClient() // Fetch and log the client after authentication
    }
  }, [isAuthenticated, hasFetchedClient, handleUserSession, oidcUser])

  const contextValue = useMemo(
    () => ({
      user,
      isAuthenticated,
      isInitializing,
      signIn: signinRedirect,
      signOut: signoutRedirect,
      getTokenSilently
    }),
    [user, isAuthenticated, isInitializing, signinRedirect, signoutRedirect, getTokenSilently]
  )

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
}

export const useAuth = () => {
  const context = useContext(AuthContext)!
  return context
}
